import { ApolloError, gql, useMutation } from '@apollo/client';
import {
  createDocumentAnalysis as CREATE_DOCUMENT_ANALYSIS,
  onUpdateDocumentAnalysis as ON_UPDATE_DOCUMENT_ANALYSIS,
  DocumentAnalysis,
  OnUpdateDocumentAnalysisSubscription,
  TaskCategory,
} from '@admiin-com/ds-graphql';
import { useEntityId } from '../../../features/hooks/useEntityContext/useEntityContext';
import React from 'react';
import { useDefaultValueForFromTo } from '../../../features/providers/TaskCreationProvider/TaskCreationProvider';
import { transformDocumentAnalysisToBill } from '../../PayBill/Upload/transformer';
import { isBillLoading } from '../../../helpers/tasks';
import { useSnackbar } from '@admiin-com/ds-web';
import { Bill } from '../../PayBill/PayBillCreateForm';
// Legacy page-count guard kept for reference; scanning limits were lifted but the implementation is preserved for quick rollback.
// import { getPageCountFromPdf } from '../../../helpers/signature';
// import { MAX_PAGES_FOR_TRIGER_SCANNING } from '../../../constants/config';
import { t } from 'i18next';
import { useDefaultFrom } from '../../PayBill/hooks/useDefaultFrom';
import { useSubscriptionWithReconnect } from '../../../hooks/useSubscriptionWithReconnect/useSubscriptionWithReconnect';
import {
  smartMergeDocumentAnalysis,
  identifyScanningType,
} from '../../../helpers/documentAnalysisMerge';

export const useScanningTax = () => {
  const getScanningErrorMessage = React.useCallback((error: unknown) => {
    const fallbackMessage = 'Error scanning document';

    if (error instanceof ApolloError) {
      const graphQLErrorMessage = error.graphQLErrors
        .map(({ message, extensions }) => {
          const code =
            typeof extensions?.code === 'string' ? extensions.code : undefined;

          if (code) {
            return `${code}: ${message}`;
          }

          return message;
        })
        .filter(Boolean)
        .join('\n');

      if (graphQLErrorMessage) {
        return graphQLErrorMessage;
      }

      const networkErrorMessage = (
        error.networkError as { message?: string } | undefined
      )?.message;
      if (networkErrorMessage) {
        return networkErrorMessage;
      }

      if (error.message) {
        return error.message;
      }
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return fallbackMessage;
  }, []);

  const [scanningKeys, setScanningKeys] = React.useState<Set<string>>(
    new Set()
  );
  const [scannedResults, setScannedResults] = React.useState<
    DocumentAnalysis[]
  >([]);
  const [skippedKeys, setSkippedKeys] = React.useState<Set<string>>(new Set());
  const [assignedEntityNames, setAssignedEntityNames] = React.useState<
    Map<string, string>
  >(new Map());
  const [referenceBaseMap, setReferenceBaseMap] = React.useState<
    Map<string, string>
  >(new Map());
  const entityId = useEntityId();
  const [createDocumentAnalysis] = useMutation(gql(CREATE_DOCUMENT_ANALYSIS));
  const showSnackbar = useSnackbar();
  const entity = useDefaultValueForFromTo();
  const defaultFrom = useDefaultFrom();
  const entityName = React.useMemo(() => {
    console.log('useScanningTax entity: ', entity);
    if (!entity) {
      return '';
    }

    const maybeName =
      (entity as any)?.legalName ||
      (entity as any)?.name ||
      (entity as any)?.tradingName;

    return typeof maybeName === 'string' ? maybeName.trim() : '';
  }, [entity]);

  const getDocumentKey = React.useCallback((analysis: DocumentAnalysis) => {
    return (
      analysis.fileKey ?? analysis.documents?.[0]?.key ?? analysis.id ?? null
    );
  }, []);

  const extractEntityName = React.useCallback((maybeEntity: any) => {
    if (!maybeEntity) {
      return '';
    }

    const maybeName =
      maybeEntity?.legalName ?? maybeEntity?.name ?? maybeEntity?.tradingName;

    return typeof maybeName === 'string' ? maybeName.trim() : '';
  }, []);

  const stripEntityPrefix = React.useCallback(
    (reference: string, knownEntityNames: string[]) => {
      const trimmedReference = reference.trim();
      if (!trimmedReference) {
        return '';
      }

      const normalizedKnownNames = knownEntityNames
        .filter((name): name is string => Boolean(name))
        .map((name) => name.toLowerCase());

      const matchKnownPrefix = normalizedKnownNames.find((normalizedName) => {
        return (
          trimmedReference.toLowerCase().startsWith(`${normalizedName} - `) ||
          trimmedReference.toLowerCase().startsWith(`${normalizedName}-`)
        );
      });

      if (matchKnownPrefix) {
        const originalName =
          knownEntityNames[normalizedKnownNames.indexOf(matchKnownPrefix)];
        if (originalName) {
          const withSeparator = `${originalName} - `;
          if (trimmedReference.startsWith(withSeparator)) {
            return trimmedReference.slice(withSeparator.length).trim();
          }
          const withoutSpaceSeparator = `${originalName}-`;
          if (trimmedReference.startsWith(withoutSpaceSeparator)) {
            return trimmedReference.slice(withoutSpaceSeparator.length).trim();
          }
        }
      }

      const spacedSeparatorIndex = trimmedReference.indexOf(' - ');
      if (spacedSeparatorIndex > -1) {
        return trimmedReference
          .slice(spacedSeparatorIndex + ' - '.length)
          .trim();
      }

      return trimmedReference;
    },
    []
  );

  const updateReferenceTracking = React.useCallback(
    (analysis: DocumentAnalysis) => {
      const documentKey = getDocumentKey(analysis);
      if (!documentKey) {
        return;
      }

      const matchedEntityName = extractEntityName(analysis.matchedEntity);

      if (matchedEntityName) {
        setAssignedEntityNames((prev) => {
          if (prev.has(documentKey)) {
            return prev;
          }

          const next = new Map(prev);
          next.set(documentKey, matchedEntityName);
          return next;
        });
      }

      const reference = analysis.task?.reference;
      if (!reference) {
        return;
      }

      const knownNames = [
        assignedEntityNames.get(documentKey) ?? '',
        matchedEntityName,
        entityName,
      ].filter((name): name is string => Boolean(name));

      const baseReference = stripEntityPrefix(reference, knownNames);

      setReferenceBaseMap((prev) => {
        const existing = prev.get(documentKey);
        if (existing === baseReference) {
          return prev;
        }

        const next = new Map(prev);
        next.set(documentKey, baseReference);
        return next;
      });
    },
    [
      assignedEntityNames,
      entityName,
      extractEntityName,
      getDocumentKey,
      stripEntityPrefix,
    ]
  );

  const prefixTaxReferenceWithEntityName = React.useCallback(
    (analysis: DocumentAnalysis): DocumentAnalysis => {
      if (!analysis) {
        return analysis;
      }

      const documentKey = getDocumentKey(analysis);
      const assignedName = documentKey
        ? assignedEntityNames.get(documentKey)
        : undefined;
      const matchedEntityName = extractEntityName(analysis.matchedEntity);
      console.log('assignedName: ', assignedName);
      console.log('matchedEntityName: ', matchedEntityName);
      console.log('entityName: ', entityName);
      const finalEntityName = assignedName ?? matchedEntityName ?? entityName;

      if (!finalEntityName) {
        return analysis;
      }

      const category = analysis.category ?? analysis.task?.category;
      if (category !== TaskCategory.TAX) {
        return analysis;
      }

      const reference = analysis.task?.reference;
      if (!reference) {
        return analysis;
      }

      const knownNamesForBase = [
        assignedName,
        matchedEntityName,
        entityName,
      ].filter((name): name is string => Boolean(name));

      const baseReference = documentKey
        ? referenceBaseMap.get(documentKey) ??
          stripEntityPrefix(reference, knownNamesForBase)
        : stripEntityPrefix(reference, knownNamesForBase);

      const trimmedReference = baseReference.trim();
      if (!trimmedReference) {
        return analysis;
      }

      const normalizedName = finalEntityName.toLowerCase();
      const normalizedReference = reference.trim().toLowerCase();

      if (
        normalizedReference.startsWith(`${normalizedName} -`) ||
        normalizedReference.startsWith(`${normalizedName}-`)
      ) {
        return analysis;
      }

      if (!analysis.task) {
        return analysis;
      }

      const prefixedReference = `${finalEntityName} - ${trimmedReference}`;

      return {
        ...analysis,
        task: {
          ...analysis.task,
          reference: prefixedReference,
        },
      };
    },
    [
      assignedEntityNames,
      entityName,
      extractEntityName,
      getDocumentKey,
      referenceBaseMap,
      stripEntityPrefix,
    ]
  );

  const handleAssignedEntityChange = React.useCallback(
    (documentKey: string | null | undefined, entity: any) => {
      if (!documentKey) {
        return;
      }

      const name = extractEntityName(entity);

      setAssignedEntityNames((prev) => {
        const next = new Map(prev);
        if (name) {
          next.set(documentKey, name);
        } else {
          next.delete(documentKey);
        }
        return next;
      });
    },
    [extractEntityName]
  );

  const handleSkipScanning = (documentKey: string | null) => {
    if (documentKey) {
      setSkippedKeys((prev) => new Set([...prev, documentKey]));
    }
  };

  const triggerScanning = async (documentKey: string, _fileUrl: string) => {
    // Legacy page-count warning retained for potential future use now that scanning supports more pages.
    // try {
    //   const pageCount = await getPageCountFromPdf(fileUrl);
    //   if (pageCount > MAX_PAGES_FOR_TRIGER_SCANNING) {
    //     showSnackbar({
    //       message: t('scanningLimitedToFirstPages', {
    //         ns: 'taskbox',
    //         maxPages: MAX_PAGES_FOR_TRIGER_SCANNING,
    //       }),
    //       severity: 'info',
    //     });
    //   }
    // } catch (error) {
    //   console.error('triggerScanning page count error: ', error);
    // }

    try {
      setScanningKeys((prev) => new Set([...prev, documentKey]));
      await createDocumentAnalysis({
        variables: {
          input: {
            entityId,
            fileKey: documentKey,
            category: TaskCategory.TAX,
          },
        },
      });
    } catch (err) {
      console.error('triggerScanning onError: ', err);
      setScanningKeys((prev) => {
        const newSet = new Set(prev);
        newSet.delete(documentKey);
        return newSet;
      });
      showSnackbar({
        message: getScanningErrorMessage(err),
        severity: 'error',
      });
    }
  };

  // Use the reusable subscription hook with reconnection
  useSubscriptionWithReconnect<OnUpdateDocumentAnalysisSubscription>(
    gql(ON_UPDATE_DOCUMENT_ANALYSIS),
    {
      variables: {
        entityId: entityId,
      },
      fetchPolicy: 'network-only',
      skip: !entityId,
      shouldResubscribe: true,
      onData: (data) => {
        const updatedData = data.data.data?.onUpdateDocumentAnalysis;
        console.log('ON_UPDATE_DOCUMENT_ANALYSIS updatedData: ', updatedData);

        if (updatedData) {
          updateReferenceTracking(updatedData);

          setScannedResults((prev) => {
            const existingIndex = prev.findIndex(
              (result) => result.id === updatedData.id
            );

            if (existingIndex >= 0) {
              // Use smart merge to preserve data from different scanning types
              const existingResult = prev[existingIndex];
              const mergedResult = smartMergeDocumentAnalysis(
                existingResult,
                updatedData
              );

              console.log(
                'Tax scanning update - type:',
                identifyScanningType(existingResult, updatedData)
              );

              const newResults = [...prev];
              newResults[existingIndex] = mergedResult;
              return newResults;
            } else {
              // New result, just add it
              return [...prev, updatedData];
            }
          });
        }
      },
      onError: (err) => {
        console.error('DocumentAnalysis subscription error (Tax):', err);
        // Clear scanning keys on error
        setScanningKeys(new Set());
      },
      onReconnect: () => {
        console.log('Tax scanning subscription attempting to reconnect...');
        showSnackbar({
          message: t('reconnectingToScanningService', {
            ns: 'info',
            defaultValue: 'Reconnecting to scanning service...',
          }),
          severity: 'info',
        });
      },
      onReconnectSuccess: () => {
        console.log('Tax scanning subscription reconnected successfully');
        showSnackbar({
          message: t('scanningServiceReconnected', {
            ns: 'info',
            defaultValue: 'Scanning service reconnected',
          }),
          severity: 'success',
        });
      },
    }
  );

  React.useEffect(() => {
    scannedResults.forEach((result) => {
      if (!isBillLoading(result) && result.fileKey) {
        setScanningKeys((prev) => {
          const newSet = new Set(prev);
          newSet.delete(result.fileKey!);
          return newSet;
        });
      }
    });
  }, [scannedResults]);

  const results: Bill[] = React.useMemo(
    () =>
      scannedResults.map((scannedResult) => {
        const analysisWithReference =
          prefixTaxReferenceWithEntityName(scannedResult);

        return {
          ...transformDocumentAnalysisToBill(
            analysisWithReference,
            defaultFrom
          ),
          to: entity,
        };
      }),
    [scannedResults, defaultFrom, entity, prefixTaxReferenceWithEntityName]
  );

  const isScanning = scanningKeys.size > 0;

  return {
    trigger: triggerScanning,
    onAssignEntity: handleAssignedEntityChange,
    data: results,
    loading: isScanning,
    scanningKeys,
    skippedKeys,
    handleSkipScanning,
  };
};
